import React, { useEffect, useState, useCallback } from "react";
import Menu from "./Menu/Menu";
import useSize from "../../../Utils/useSize/useSize";
import Backend from "../../../Backend/Backend";
import toast from "react-hot-toast";
import Loading from "../../../Utils/Loading/Loading";
import useSound from "use-sound";
import ErrorSoundFile from "../../../Sound/Error.wav";
import NotFound from "../../../Utils/NotFound/NotFound";
import TotalCard from "./TotalCard/TotalCard";
import TradeCard from "./TradeCard";
import moment from "moment";
import Filter from "./Filter/Filter";

const backend = await new Backend().init();

export default function Trade({ user_id }) {
  const userType = Number(localStorage.getItem("user_type") || 7);

  const [playError] = useSound(ErrorSoundFile);
  const windowSize = useSize();

  // ---------------------------------------------------
  // STATES
  // ---------------------------------------------------
  const [trade, setTrade] = useState([]);
  const [tradeSearch, setTradeSearch] = useState([]);

  const [commission, setCommission] = useState(0);
  const [booked, setBooked] = useState(0);
  const [nseCash, setNseCash] = useState(0);
  const [rent, setRent] = useState(0);
  const [tradeDelete, setTradeDelete] = useState(false)

  const [loading, setLoading] = useState(false);
  const [isFetchedOnce, setIsFetchedOnce] = useState(false);

  const [start, setStart] = useState(
    moment().startOf("week").format("YYYY-MM-DD 00:00:00")
  );
  const [end, setEnd] = useState(
    moment().endOf("week").format("YYYY-MM-DD 23:59:59")
  );

  // ---------------------------------------------------
  // HANDLE API RESPONSE
  // ---------------------------------------------------
  const handleTradeResponse = useCallback(
    (r) => {
      if (!r) return;

      if (r.error === "False") {
        setTrade(r.order || []);
        setTradeSearch(r.order || []);
        setCommission(r.commission || 0);
        setBooked(r.booked || 0);
        setNseCash(r.nse_cash || 0);
        setRent(r.rent || 0);
        setTradeDelete(r.trade_delete || false);
      } else {
        playError();
        toast.error(r.message || "Something went wrong");
        if (r.logout === 1 && window.LogOut) window.LogOut();
      }
    },
    [playError]
  );

  // ---------------------------------------------------
  // BUILD PAYLOAD (IMPORTANT — FIXED)
  // ---------------------------------------------------
  const buildPayload = useCallback(
    (override = {}) => ({
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
      user_id: user_id,

      start: override.start || start,
      end: override.end || end,
    }),
    [user_id, start, end]
  );

  // ---------------------------------------------------
  // FETCH TRADES
  // ---------------------------------------------------
  const fetchTrade = useCallback(
    async (override = {}) => {
      setLoading(true);
      try {
        const payload = buildPayload(override);
        const r = await backend.trade(payload);
        handleTradeResponse(r);
      } catch (err) {
        playError();
        toast.error("Network error while loading trades");
      } finally {
        setLoading(false);
        setIsFetchedOnce(true);
      }
    },
    [buildPayload, handleTradeResponse, playError]
  );

  // ---------------------------------------------------
  // INITIAL LOAD
  // ---------------------------------------------------
  useEffect(() => {
    fetchTrade();
  }, [fetchTrade]);

  // ---------------------------------------------------
  // FILTER HANDLER (FIXED)
  // ---------------------------------------------------
  const handleFilter = useCallback(
    ({ start, end }) => {
      setStart(start);
      setEnd(end);
      fetchTrade({ start, end }); // APPLY NEW DATES NOW ✔
    },
    [fetchTrade]
  );

  // ---------------------------------------------------
  // SEARCH
  // ---------------------------------------------------
  const search = useCallback(
    (query) => {
      const q = query.trim().toLowerCase();
      setTrade(
        q.length > 1
          ? tradeSearch.filter((t) =>
              t.symbol?.toLowerCase().includes(q.toLowerCase())
            )
          : tradeSearch
      );
    },
    [tradeSearch]
  );

  // ---------------------------------------------------
  // SORT HANDLER
  // ---------------------------------------------------
  const handleSort = useCallback((sorted) => setTrade(sorted), []);

  // ---------------------------------------------------
  // RENDER
  // ---------------------------------------------------
  return (
    <div
      style={{
        width: windowSize[0] - 100,
        height: windowSize[1] - 170,
      }}
    >
      {/* FILTER */}
      <Filter
        search={search}
        onFilter={handleFilter}
        records={trade.length}
        userType={userType}
      />

      {/* TOTAL CARD */}
      <TotalCard
        commission={commission}
        booked={booked}
        rent={rent}
        nseCash={nseCash}
        userType={userType}
        trade={trade}
      />

      {/* MENU */}
      <Menu
        userType={userType}
        trade_delete={tradeDelete}
        trade_search={tradeSearch}
        handlewSort={handleSort}
      />

      {/* TRADE LIST */}
      <div
        style={{
          width: windowSize[0] - 100,
          height: windowSize[1] - 330,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <Loading height={windowSize[1] - 330} />
        ) : trade.length > 0 ? (
          <div
            style={{
              width: windowSize[0] - 100,
              height: windowSize[1] - 330,
              overflowY: "auto",
            }}
          >
            {trade.map((item) => (
              <TradeCard
                key={item.id}
                item={item}
                reload={fetchTrade}
                trade_delete={tradeDelete}
                userType={userType}
              />
            ))}
          </div>
        ) : isFetchedOnce ? (
          <NotFound
            height={windowSize[1] - 330}
            title="Nothing here"
            subtext="No Trade Found."
          />
        ) : null}
      </div>
    </div>
  );
}
